/*
 * Welham & Co FAQ (session W2): deep links on every FAQ block, and the FAQ
 * page's search (Luke, L-49, 7 Oct: "should be able to search all faq
 * content"). Markup: blocks/wc-faq.liquid and snippets/wc-faq-search.liquid.
 * Proved offline by tests/wc-faq-search.test.mjs on the Mon 12 copy.
 *
 * SEARCH, in one paragraph. Every question and answer on the page is indexed
 * once, from the DOM the block rendered, so a closed row is searched exactly
 * like an open one and nothing is fetched. A query is cut into words, the
 * filler words are dropped ("how long does delivery take" is "long delivery
 * take"), and each word matches the START of a word in the row, so "frame"
 * finds "framed" and "8x10" finds "8 × 10 in". Rows holding every word are
 * shown; if none holds every word, the rows holding the most are shown and the
 * status says so, because a long natural question should not end in "nothing
 * found" when one word was all that missed. Spelling is the shopper's, not the
 * domain's: a US visitor on the UK site typing "color" finds "colour".
 *
 * The rows a search opened are closed again when it is cleared; a row the
 * shopper opened by hand is left as it was.
 */
(() => {
  if (window.wcFaqReady) return;
  window.wcFaqReady = true;

  const STOP = new Set(
    (
      "a an and any are as at be been but by can could did do does doing for from get got had has have how i if " +
      "im in into is it its me my of on or our should so than that the their them then there these they this " +
      "to too us was we were what when where which who whom why will with would you your youre yours ill id ive"
    ).split(" "),
  );

  // Spellings and words that mean the same thing on one domain and another.
  const SAME = [
    ["color", "colour", "colors", "colours"],
    ["favorite", "favourite"],
    ["personalized", "personalised", "personalize", "personalise"],
    ["center", "centre"],
    ["jewelry", "jewellery"],
    ["mom", "mum"],
    ["gray", "grey"],
    ["canceled", "cancelled", "cancel", "cancellation"],
    ["watercolor", "watercolour"],
    ["matte", "matt"],
    ["shipping", "delivery", "ship", "shipped", "deliver", "delivered", "postage"],
    ["price", "prices", "cost", "costs", "pricing"],
    // The $0 Upfront answer says "charged" and "card", never "pay".
    ["pay", "payment", "payments", "paying", "paid", "charge", "charged", "charges", "card"],
  ];
  const sameAs = new Map();
  for (const group of SAME) for (const word of group) sameAs.set(word, group);

  const fold = (text) =>
    text
      .normalize("NFKD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[‘’]/g, "'")
      .replace(/(\d)\s*[x×]\s*(\d)/g, "$1x$2");
  const wordsOf = (text) =>
    fold(text)
      .split(/[^\p{L}\p{N}]+/u)
      .filter(Boolean);

  /** Each searched word, with the forms that also count for it. */
  const termsOf = (query) => {
    const seen = new Set();
    const terms = [];
    for (const word of wordsOf(query.replace(/'/g, ""))) {
      if (STOP.has(word) || (word.length < 2 && !/\d/.test(word)) || seen.has(word)) continue;
      seen.add(word);
      const forms = new Set(sameAs.get(word) || [word]);
      if (word.length > 3 && word.endsWith("s")) forms.add(word.slice(0, -1));
      terms.push([...forms]);
    }
    return terms;
  };

  const escapeRe = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  /** A form as it may appear in the page's own text: "8x10" is also "8 × 10". */
  const pattern = (form) => escapeRe(form).replace(/(\d)x(\d)/g, "$1\\s*[x×]\\s*$2");

  const fill = (text, key, value) => (text || "").split(`[${key}]`).join(value);

  const canHighlight = typeof CSS !== "undefined" && "highlights" in CSS && typeof Highlight === "function";

  function setUpSearch(root) {
    const form = root.querySelector("[data-wc-faq-search]");
    if (!form) return;
    const input = form.querySelector('input[type="search"]');
    const clear = form.querySelector("[data-wc-faq-clear]");
    const status = form.querySelector("[data-wc-faq-status]");
    const empty = root.querySelector("[data-wc-faq-empty]");
    const nav = root.querySelector(".wc-faq__nav");
    const groups = [...root.querySelectorAll(".wc-faq__group")];
    const suggest = root.querySelector("[data-wc-faq-suggest]");
    const market = (root.dataset.wcFaq || "").split(":")[0];
    // A link the settings rule out for this market is never offered, whatever was typed.
    const offers = suggest
      ? [...suggest.querySelectorAll("li[data-words]")]
          .filter((li) => !(li.dataset.off || "").split(",").some((m) => m.trim() === market))
          .map((li) => ({
            li,
            label: li.textContent.trim(),
            phrases: li.dataset.words
              .split(",")
              .map(wordsOf)
              .filter((phrase) => phrase.length),
          }))
      : [];
    /** "how much is it" says "how much"; each word may be the start of the typed one ("refunds"). */
    const says = (said, phrase) =>
      said.some((_, i) => phrase.every((word, j) => said[i + j] !== undefined && said[i + j].startsWith(word)));

    const rows = [...root.querySelectorAll("details.wc-faq__row")].map((row) => {
      const parts = [row.querySelector(".wc-faq__question-text"), row.querySelector(".wc-faq__answer")].filter(Boolean);
      return { row, parts, words: wordsOf(parts.map((part) => part.textContent).join(" ")) };
    });
    const openedBySearch = new Set();
    let timer = 0;

    const hits = (entry, forms) => forms.some((form) => entry.words.some((word) => word.startsWith(form)));

    const paint = (shown, terms) => {
      if (!canHighlight) return;
      if (!shown.length) return CSS.highlights.delete("wc-faq-hit");
      const re = new RegExp(`(?<![\\p{L}\\p{N}])(?:${terms.flat().map(pattern).join("|")})[\\p{L}\\p{N}]*`, "giu");
      const ranges = [];
      for (const entry of shown) {
        for (const part of entry.parts) {
          const walker = document.createTreeWalker(part, NodeFilter.SHOW_TEXT);
          for (let node = walker.nextNode(); node; node = walker.nextNode()) {
            for (const match of node.data.matchAll(re)) {
              const range = new Range();
              range.setStart(node, match.index);
              range.setEnd(node, match.index + match[0].length);
              ranges.push(range);
            }
          }
        }
      }
      CSS.highlights.set("wc-faq-hit", new Highlight(...ranges));
    };

    const show = (visible) => {
      for (const entry of rows) {
        const on = visible.has(entry);
        entry.row.hidden = !on;
        if (on && !entry.row.open) {
          entry.row.open = true;
          openedBySearch.add(entry.row);
        } else if (!on && openedBySearch.has(entry.row)) {
          entry.row.open = false;
          openedBySearch.delete(entry.row);
        }
      }
      for (const group of groups) group.hidden = !group.querySelector("details.wc-faq__row:not([hidden])");
    };

    const reset = () => {
      for (const entry of rows) entry.row.hidden = false;
      for (const row of openedBySearch) row.open = false;
      openedBySearch.clear();
      for (const group of groups) group.hidden = false;
      if (nav) nav.hidden = false;
      if (empty) empty.hidden = true;
      if (suggest) suggest.hidden = true;
      status.textContent = "";
      delete root.dataset.wcFaqSearching;
      if (canHighlight) CSS.highlights.delete("wc-faq-hit");
    };

    const run = () => {
      clearTimeout(timer);
      const query = input.value.trim();
      form.toggleAttribute("data-empty", input.value === "");
      const terms = termsOf(query);
      if (!terms.length) return reset();

      root.dataset.wcFaqSearching = "";
      const scored = rows.map((entry) => [entry, terms.filter((forms) => hits(entry, forms)).length]);
      const best = Math.max(0, ...scored.map(([, score]) => score));
      const shown = best ? scored.filter(([, score]) => score === best).map(([entry]) => entry) : [];
      show(new Set(shown));
      if (nav) nav.hidden = true;
      if (empty) empty.hidden = shown.length > 0;

      // Only when the page fell short: nothing found, or nothing with every word.
      const said = wordsOf(query);
      const offered =
        shown.length && best === terms.length ? [] : offers.filter((o) => o.phrases.some((p) => says(said, p)));
      for (const o of offers) o.li.hidden = !offered.includes(o);
      if (suggest) suggest.hidden = !offered.length;

      if (!shown.length) {
        status.textContent = fill(form.dataset.none, "query", query);
      } else if (best < terms.length) {
        status.textContent = fill(form.dataset.some, "count", shown.length);
      } else {
        status.textContent = shown.length === 1 ? form.dataset.one : fill(form.dataset.other, "count", shown.length);
      }
      // Said to a screen reader; the box below already shows it to everyone else.
      if (offered.length) {
        const aside = document.createElement("span");
        aside.className = "wc-faq__sr";
        aside.textContent = ` ${suggest.dataset.heading} ${offered.map((o) => o.label).join(", ")}.`;
        status.append(aside);
      }
      paint(shown, terms);
    };

    const wipe = () => {
      input.value = "";
      run();
    };

    input.addEventListener("input", () => {
      form.toggleAttribute("data-empty", input.value === "");
      clearTimeout(timer);
      timer = setTimeout(run, 200);
    });
    input.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && input.value !== "") {
        event.preventDefault();
        wipe();
      }
    });
    // Enter runs the search at once and moves to the first answer it found,
    // so a keyboard user lands on the result rather than tabbing to it.
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      run();
      root.querySelector("details.wc-faq__row:not([hidden]) > summary")?.focus();
    });
    clear?.addEventListener("click", () => {
      wipe();
      input.focus();
    });
    // A row the search opened and the shopper then closed is theirs now.
    root.addEventListener(
      "toggle",
      (event) => {
        if (!event.target.open) openedBySearch.delete(event.target);
      },
      true,
    );

    return { wipe };
  }

  function setUp(root) {
    if (root.dataset.wcFaqReady !== undefined) return;
    root.dataset.wcFaqReady = "";
    const search = root.dataset.wcFaqMode === "page" ? setUpSearch(root) : null;
    const calm = matchMedia("(prefers-reduced-motion: reduce)");

    // Deep links: /pages/faq#faq-<handle> opens that question and scrolls to
    // it, so a support reply or an AI answer can point at one answer. Opening a
    // row by hand writes its hash with replaceState (no history entry, no
    // scroll), so the address bar always holds a link to what is open. Not
    // while a search is opening rows: that would be the search's link, not the
    // shopper's.
    const openFromHash = (event) => {
      const id = decodeURIComponent(location.hash.slice(1));
      if (!id.startsWith("faq-")) return;
      const row = root.querySelector(`details[id="${CSS.escape(id)}"]`);
      if (!row) return;
      if (row.hidden) search?.wipe();
      row.open = true;
      row.scrollIntoView({ block: "start", behavior: event && !calm.matches ? "smooth" : "auto" });
    };
    root.addEventListener(
      "toggle",
      (event) => {
        const row = event.target;
        if (
          row.matches?.("details.wc-faq__row") &&
          row.open &&
          root.dataset.wcFaqMode === "page" &&
          root.dataset.wcFaqSearching === undefined
        ) {
          history.replaceState(history.state, "", `#${row.id}`);
        }
      },
      true,
    );
    window.addEventListener("hashchange", openFromHash);
    openFromHash(null);
  }

  const start = () => document.querySelectorAll(".wc-faq").forEach(setUp);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
})();
